import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    View, StyleSheet, Text, TouchableOpacity, ScrollView,
    Animated, Dimensions, Platform, Alert, TextInput,
    ActivityIndicator, RefreshControl, Modal, Linking,
    KeyboardAvoidingView
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import { Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import {
    getSessionsByStatus, getLiveKitToken, createLiveKitRoom,
    buildRoomName, LIVEKIT_WS_URL, scheduleMentorSession,
    endSession
} from '../../services/livekit';

import MentorScheduleModal from './components/MentorScheduleModal';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const MENTOR_PRIMARY = '#7c3aed';
const MENTOR_BG = '#FAF5FF';

// ── Mock active rooms for the placeholder / demo ───────────────────────
const MOCK_SPACES = []; // Empty mocks

const MentorSpacesScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const { user } = useAuth();

    const [activeTab, setActiveTab] = useState('live'); // 'live', 'upcoming', 'history'
    const [spaces, setSpaces] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [joiningId, setJoiningId] = useState(null);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
    const [newSpaceTopic, setNewSpaceTopic] = useState('');
    const [creating, setCreating] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const isMentor = !!(
        user?.role?.toLowerCase() === 'mentor' ||
        user?.role?.toLowerCase() === 'admin' ||
        user?.isMentor === true ||
        user?.isMentor === 1 ||
        user?.userType === 'mentor'
    );

    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }).start();
    }, []);

    const route = useRoute();
    const [hasAutoJoined, setHasAutoJoined] = useState(false);

    // Fetch data whenever activeTab changes or when the screen regains focus
    useFocusEffect(
        useCallback(() => {
            loadRooms();
        }, [activeTab, loadRooms])
    );

    const loadRooms = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getSessionsByStatus(activeTab);
            if (data && data.length > 0) {
                setSpaces(data);
            } else {
                // If no real data, we could show mocks or just empty state
                setSpaces([]);
            }
        } catch (err) {
            console.error('[Spaces] Load error:', err);
            // keep existing or mock data
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    // Auto-join logic if redirected from notification
    useEffect(() => {
        if (spaces.length > 0 && route.params?.spaceId && !hasAutoJoined) {
            const spaceToJoin = spaces.find(s => String(s.id) === String(route.params.spaceId));
            if (spaceToJoin && (spaceToJoin.isLive || spaceToJoin.status === 'ongoing')) {
                setHasAutoJoined(true);
                handleJoinSpace(spaceToJoin);
            }
        }
    }, [spaces, route.params?.spaceId, hasAutoJoined]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await loadRooms();
        setRefreshing(false);
    }, [loadRooms]);

    const handleJoinSpace = async (space) => {
        try {
            setLoading(true);
            setJoiningId(space.id);
            const participantName = user?.name || user?.email || 'Anonymous';
            const participantId = String(user?.id || Date.now());

            // Check if user is the mentor for this session
            const isHost = space.mentorId == user?.id || space.mentorName === (user?.name || user?.email);
            console.log('[Spaces] Joining space:', space.roomName, 'as', isHost ? 'host' : 'participant');

            const isOffline = space.isLive && space.mentorOnline === false;

            // If mentor is starting a scheduled room or resuming an offline room, transition it
            if (isHost && (space.status === 'scheduled' || isOffline)) {
                console.log('[Spaces] Activating/Resuming room...');
                try {
                    await createLiveKitRoom({
                        roomName: space.roomName,
                        mentorId: user?.id,
                        topic: space.topic
                    });
                } catch (creErr) {
                    console.warn('[Spaces] Could not activate room specifically, continuing to token request');
                }
            }

            let token, wsUrl;
            try {
                const res = await getLiveKitToken({
                    roomName: space.roomName,
                    participantName,
                    participantId,
                    role: isHost ? 'host' : 'participant'
                });
                token = res.token;
                wsUrl = res.wsUrl || LIVEKIT_WS_URL;
            } catch (apiErr) {
                // Check if the server returned 403 (account deactivated)
                const is403 = apiErr?.status === 403 ||
                    apiErr?.response?.status === 403 ||
                    String(apiErr?.message || '').includes('deactivated');

                if (is403) {
                    token = 'ACCOUNT_DEACTIVATED';
                } else {
                    // Generic API failure — use demo placeholder
                    console.warn('[Spaces] Token API not ready, using demo mode');
                    token = 'DEMO_TOKEN_NOT_REAL';
                }
                wsUrl = LIVEKIT_WS_URL;
            }

            navigation.navigate('MentorLiveRoom', {
                roomName: space.roomName,
                topic: space.topic,
                mentorName: space.mentorName,
                token,
                wsUrl,
                participantName,
                isHost: isHost,
                sessionId: space.id
            });
        } catch (err) {
            console.error('[Spaces] Join error:', err);
            Alert.alert('Error', 'Could not join the space. Please try again.');
        } finally {
            setLoading(false);
            setJoiningId(null);
        }
    };

    const searchUsers = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const { getData } = require('../../services/api');
            const data = await getData(`/user/search?q=${encodeURIComponent(query)}`);
            setSearchResults(data || []);
        } catch (err) {
            console.error('[Spaces] User search error:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const toggleUserSelection = (u) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(item => item.id === u.id);
            if (isSelected) {
                return prev.filter(item => item.id !== u.id);
            } else {
                return [...prev, u];
            }
        });
    };

    const handleCreateSpace = async () => {
        if (!newSpaceTopic.trim()) {
            Alert.alert('Topic Required', 'Please enter a topic for your space.');
            return;
        }
        setCreating(true);
        try {
            const mentorName = user?.name || 'Mentor';
            const roomName = buildRoomName(mentorName, newSpaceTopic);
            const participantName = mentorName;
            const participantId = String(user?.id || Date.now());

            let token, wsUrl;
            try {
                const allowedUserIds = selectedUsers.map(u => u.id);
                await createLiveKitRoom({
                    roomName,
                    mentorId: user?.id,
                    topic: newSpaceTopic,
                    allowedUserIds: allowedUserIds.length > 0 ? allowedUserIds : null
                });
                const res = await getLiveKitToken({ roomName, participantName, participantId, role: 'host' });
                token = res.token;
                wsUrl = res.wsUrl || LIVEKIT_WS_URL;
            } catch (apiErr) {
                // Check if the server returned 403 (account deactivated)
                const is403 = apiErr?.status === 403 ||
                    apiErr?.response?.status === 403 ||
                    String(apiErr?.message || '').includes('deactivated');

                if (is403) {
                    token = 'ACCOUNT_DEACTIVATED';
                } else {
                    token = 'DEMO_TOKEN_NOT_REAL';
                }
                wsUrl = LIVEKIT_WS_URL;
            }

            setCreateModalVisible(false);
            setNewSpaceTopic('');

            navigation.navigate('MentorLiveRoom', {
                roomName,
                topic: newSpaceTopic,
                mentorName: mentorName,
                token,
                wsUrl,
                participantName,
                isHost: true,
            });
        } catch (err) {
            Alert.alert('Error', 'Could not create the space. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const handleScheduleSpace = async (scheduleData) => {
        console.log('[Spaces] Scheduling space:', scheduleData);
        try {
            setLoading(true);
            await scheduleMentorSession(scheduleData);
            Alert.alert('Success', 'Mentoring session scheduled successfully!');
            setScheduleModalVisible(false);
            loadRooms();
        } catch (err) {
            console.error('[Spaces] Schedule error:', err);
            Alert.alert('Error', 'Failed to schedule the session.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSpace = async (space) => {
        Alert.alert(
            'Remove Space?',
            `Are you sure you want to remove "${space.topic}"? This will cancel the session.`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Remove',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            setLoading(true);
                            await endSession(space.id);
                            loadRooms();
                        } catch (err) {
                            console.error('[Spaces] Delete error:', err);
                            Alert.alert('Error', 'Failed to remove the space.');
                        } finally {
                            setLoading(false);
                        }
                    }
                }
            ]
        );
    };

    const liveSpaces = spaces.filter(s => s.isLive || s.status === 'ongoing');
    const upcomingSpaces = spaces.filter(s => !s.isLive && s.status !== 'completed' && s.status !== 'ongoing');
    const historySpaces = spaces.filter(s => s.status === 'completed');

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <LinearGradient
                    colors={['#3b0764', '#5b21b6', '#7c3aed']}
                    style={StyleSheet.absoluteFill}
                />
                {/* Decorative orbs */}
                <View style={styles.decorOrb1} />
                <View style={styles.decorOrb2} />

                <Animated.View style={[styles.headerContent, { opacity: fadeAnim }]}>
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                    </TouchableOpacity>

                    <View style={styles.headerCenter}>
                        <View style={styles.headerIconBadge}>
                            <Ionicons name="planet" size={22} color="#c4b5fd" />
                        </View>
                        <View>
                            <Text style={styles.headerTitle}>Mentor Spaces</Text>
                            <Text style={styles.headerSubtitle}>VIRTUAL ROOMS</Text>
                        </View>
                    </View>

                    {/* Create Space / Schedule Space */}
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                        {isMentor && (
                            <>
                                <TouchableOpacity
                                    style={[styles.createBtn, { backgroundColor: 'rgba(255,255,255,0.1)' }]}
                                    onPress={() => setScheduleModalVisible(true)}
                                >
                                    <Ionicons name="calendar" size={20} color="#FFFFFF" />
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.createBtn}
                                    onPress={() => setCreateModalVisible(true)}
                                >
                                    <Ionicons name="add" size={22} color="#FFFFFF" />
                                </TouchableOpacity>
                            </>
                        )}
                    </View>
                </Animated.View>

                {/* Tab Bar (Match npbc functionality with YC design) */}
                <View style={styles.tabBar}>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'live' && styles.activeTab]}
                        onPress={() => setActiveTab('live')}
                    >
                        <Text style={[styles.tabLabel, activeTab === 'live' && styles.activeTabLabel]}>Live Now</Text>
                        {liveSpaces.length > 0 && <View style={styles.tabBadge} />}
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'upcoming' && styles.activeTab]}
                        onPress={() => setActiveTab('upcoming')}
                    >
                        <Text style={[styles.tabLabel, activeTab === 'upcoming' && styles.activeTabLabel]}>Upcoming</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.tab, activeTab === 'history' && styles.activeTab]}
                        onPress={() => setActiveTab('history')}
                    >
                        <Text style={[styles.tabLabel, activeTab === 'history' && styles.activeTabLabel]}>History</Text>
                    </TouchableOpacity>
                </View>
            </View>

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={{ paddingBottom: insets.bottom + 120 }}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={MENTOR_PRIMARY}
                    />
                }
            >
                <Animated.View style={{ opacity: fadeAnim }}>
                    {loading && !refreshing ? (
                        <View style={{ marginTop: 50, alignItems: 'center' }}>
                            <ActivityIndicator size="large" color={MENTOR_PRIMARY} />
                            <Text style={{ color: '#9CA3AF', marginTop: 12 }}>Loading sessions...</Text>
                        </View>
                    ) : (
                        <>
                            {/* ── Active Tab Content ──────────── */}
                            {activeTab === 'live' && (
                                <View style={styles.section}>
                                    <View style={styles.sectionHeaderRow}>
                                        <View style={styles.liveIndicator}>
                                            <View style={styles.livePulse} />
                                            <Text style={styles.sectionTitle}>🔴 Active Sessions</Text>
                                        </View>
                                    </View>

                                    {liveSpaces.length === 0 ? (
                                        <View style={styles.emptyState}>
                                            <Text style={styles.emptyEmoji}>🌙</Text>
                                            <Text style={styles.emptyText}>No live spaces right now</Text>
                                            <Text style={styles.emptySubtext}>Check back soon or start your own!</Text>
                                        </View>
                                    ) : (
                                        liveSpaces.map(space => (
                                            <SpaceCard
                                                key={space.id}
                                                space={space}
                                                onJoin={() => handleJoinSpace(space)}
                                                isJoining={joiningId === space.id}
                                                onDelete={space.mentorId === user?.id ? () => handleDeleteSpace(space) : null}
                                            />
                                        ))
                                    )}
                                </View>
                            )}

                            {activeTab === 'upcoming' && (
                                <View style={styles.section}>
                                    <View style={styles.sectionHeaderRow}>
                                        <Text style={styles.sectionTitle}>📅 Scheduled for Later</Text>
                                    </View>
                                    {upcomingSpaces.length === 0 ? (
                                        <View style={styles.emptyState}>
                                            <Text style={styles.emptyEmoji}>⏳</Text>
                                            <Text style={styles.emptyText}>Nothing scheduled</Text>
                                            <Text style={styles.emptySubtext}>You have no upcoming mentoring sessions</Text>
                                        </View>
                                    ) : (
                                        upcomingSpaces.map(space => (
                                            <SpaceCard
                                                key={space.id}
                                                space={space}
                                                onJoin={() => {
                                                    if (isMentor) {
                                                        handleJoinSpace(space); // Mentors can start/join scheduled rooms
                                                    } else {
                                                        Alert.alert(
                                                            space.scheduledAt || 'Scheduled',
                                                            `"${space.topic}" with ${space.mentorName} is not live yet.`
                                                        );
                                                    }
                                                }}
                                                isJoining={joiningId === space.id}
                                                label={isMentor ? 'Start' : 'Notify'}
                                                onDelete={space.mentorId === user?.id ? () => handleDeleteSpace(space) : null}
                                            />
                                        ))
                                    )}
                                </View>
                            )}

                            {activeTab === 'history' && (
                                <View style={styles.section}>
                                    <View style={styles.sectionHeaderRow}>
                                        <Text style={styles.sectionTitle}>⌛ Finished Sessions</Text>
                                    </View>
                                    {historySpaces.length === 0 ? (
                                        <View style={styles.emptyState}>
                                            <Text style={styles.emptyEmoji}>📚</Text>
                                            <Text style={styles.emptyText}>No history yet</Text>
                                        </View>
                                    ) : (
                                        historySpaces.map(space => (
                                            <SpaceCard
                                                key={space.id}
                                                space={space}
                                                onJoin={() => { }}
                                                isJoining={false}
                                            />
                                        ))
                                    )}
                                </View>
                            )}
                        </>
                    )}

                    {isMentor && (
                        <View style={styles.ctaSectionList}>
                            <Text style={styles.sectionTitle}>Quick Actions</Text>
                            <View style={styles.ctaList}>
                                <TouchableOpacity
                                    style={styles.ctaListItem}
                                    onPress={() => setCreateModalVisible(true)}
                                >
                                    <View style={[styles.ctaListIcon, { backgroundColor: '#7c3aed15' }]}>
                                        <Ionicons name="radio" size={24} color={MENTOR_PRIMARY} />
                                    </View>
                                    <View style={styles.ctaListContent}>
                                        <Text style={styles.ctaListLabel}>Go Live Now</Text>
                                        <Text style={styles.ctaListSnippet}>Start an instant video room for your mentees.</Text>
                                    </View>
                                    <Ionicons name="chevron-forward" size={18} color="#D1D5DB" />
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={styles.ctaListItem}
                                    onPress={() => setScheduleModalVisible(true)}
                                >
                                    <View style={[styles.ctaListIcon, { backgroundColor: '#10B98115' }]}>
                                        <Ionicons name="calendar-outline" size={24} color="#10B981" />
                                    </View>
                                    <View style={styles.ctaListContent}>
                                        <Text style={styles.ctaListLabel}>Schedule Class</Text>
                                        <Text style={styles.ctaListSnippet}>Set a time for a future session or workshop.</Text>
                                    </View>
                                    <Ionicons name="chevron-forward" size={18} color="#D1D5DB" />
                                </TouchableOpacity>

                                <TouchableOpacity style={styles.ctaListItem}>
                                    <View style={[styles.ctaListIcon, { backgroundColor: '#3B82F615' }]}>
                                        <Ionicons name="stats-chart-outline" size={24} color="#3B82F6" />
                                    </View>
                                    <View style={styles.ctaListContent}>
                                        <Text style={styles.ctaListLabel}>Activity Insights</Text>
                                        <Text style={styles.ctaListSnippet}>Track attendance and engagement metrics.</Text>
                                    </View>
                                    <Ionicons name="chevron-forward" size={18} color="#D1D5DB" />
                                </TouchableOpacity>
                            </View>
                        </View>
                    )}
                </Animated.View>
            </ScrollView>

            {/* ── Sticky Footer ──────────── */}
            <View style={[styles.stickyFooter, { paddingBottom: insets.bottom + 10 }]}>
                <View style={styles.stickyHowRow}>
                    {[
                        { icon: 'videocam', color: '#7c3aed', label: 'Live Video', desc: 'HD streaming' },
                        { icon: 'people', color: '#10B981', label: 'Group Rooms', desc: 'Up to 50 people' },
                        { icon: 'chatbubbles', color: '#3B82F6', label: 'Live Chat', desc: 'Instant messaging' },
                        { icon: 'shield-checkmark', color: '#F59E0B', label: 'Safe Rooms', desc: 'Moderated spaces' },
                    ].map((item, i) => (
                        <View key={i} style={[styles.stickyHowCard, { borderColor: item.color + '30' }]}>
                            <View style={[styles.stickyHowIconBox, { backgroundColor: item.color + '15' }]}>
                                <Ionicons name={item.icon} size={16} color={item.color} />
                            </View>
                            <Text style={styles.stickyHowLabel} numberOfLines={1}>{item.label}</Text>
                            <Text style={styles.stickyHowDesc} numberOfLines={1}>{item.desc}</Text>
                        </View>
                    ))}
                </View>
                <Caption style={styles.footerTextSticky}>
                    Youth Connect • Mentor Spaces (Powered by{' '}
                    <Text
                        style={{ color: '#7c3aed', fontWeight: '800' }}
                        onPress={() => Linking.openURL('https://softextensions.com/')}
                    >
                        SoftEx VvoIP
                    </Text>)
                </Caption>
            </View>

            <Modal
                visible={createModalVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setCreateModalVisible(false)}
            >
                <TouchableOpacity
                    style={styles.modalOverlay}
                    activeOpacity={1}
                    onPress={() => setCreateModalVisible(false)}
                >
                    <KeyboardAvoidingView
                        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                        style={styles.keyboardAvoidingView}
                    >
                        <TouchableOpacity 
                            activeOpacity={1} 
                            style={styles.createModal}
                        >
                            <ScrollView 
                                showsVerticalScrollIndicator={false}
                                keyboardShouldPersistTaps="handled"
                                contentContainerStyle={{ paddingBottom: 20 }}
                            >
                                <View style={styles.modalHandle} />
                                <Text style={styles.modalTitle}>Start a Mentor Space</Text>
                                <Text style={styles.modalSubtitle}>
                                    Choose a topic for your live session
                                </Text>

                            <TextInput
                                style={styles.topicInput}
                                placeholder="e.g. Business Q&A, Tech Workshop..."
                                placeholderTextColor="#9CA3AF"
                                value={newSpaceTopic}
                                onChangeText={setNewSpaceTopic}
                                maxLength={60}
                                autoFocus={true}
                            />

                            {/* --- Start Focus Group (Invite Users) --- */}
                            <Text style={[styles.modalSubtitle, { marginTop: 16, marginBottom: 8 }]}>
                                Focus Group (Optional): Invite specific users
                            </Text>
                            
                            <View style={styles.userSearchBox}>
                                <Ionicons name="search" size={16} color="#9CA3AF" style={{ marginLeft: 12 }} />
                                <TextInput
                                    style={styles.userSearchInput}
                                    placeholder="Search users to invite..."
                                    placeholderTextColor="#9CA3AF"
                                    value={userSearchQuery}
                                    onChangeText={(val) => {
                                        setUserSearchQuery(val);
                                        searchUsers(val);
                                    }}
                                />
                                {isSearching && <ActivityIndicator size="small" color={MENTOR_PRIMARY} style={{ marginRight: 12 }} />}
                            </View>

                            {/* Selected Users Chips */}
                            {selectedUsers.length > 0 && (
                                <View style={styles.selectedUsersContainer}>
                                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                        {selectedUsers.map(u => (
                                            <TouchableOpacity 
                                                key={`sel-${u.id}`} 
                                                style={styles.userChip}
                                                onPress={() => toggleUserSelection(u)}
                                            >
                                                <Text style={styles.userChipText}>{u.name}</Text>
                                                <Ionicons name="close-circle" size={14} color="#FFFFFF" style={{ marginLeft: 4 }} />
                                            </TouchableOpacity>
                                        ))}
                                    </ScrollView>
                                </View>
                            )}

                            {/* Search Results */}
                            {searchResults.length > 0 && (
                                <View style={styles.searchResultsContainer}>
                                    <ScrollView style={{ maxHeight: 150 }}>
                                        {searchResults.map(u => {
                                            const isSelected = selectedUsers.some(item => item.id === u.id);
                                            return (
                                                <TouchableOpacity 
                                                    key={`res-${u.id}`} 
                                                    style={styles.searchResultItem}
                                                    onPress={() => toggleUserSelection(u)}
                                                >
                                                    <View style={styles.searchResultLeft}>
                                                        <View style={styles.searchResultAvatar}>
                                                            <Text style={{ fontSize: 10 }}>{u.name.charAt(0)}</Text>
                                                        </View>
                                                        <View>
                                                            <Text style={styles.searchResultName}>{u.name}</Text>
                                                            <Text style={styles.searchResultSub}>{u.occupation || 'Student'}</Text>
                                                        </View>
                                                    </View>
                                                    <Ionicons 
                                                        name={isSelected ? "checkbox" : "square-outline"} 
                                                        size={20} 
                                                        color={isSelected ? MENTOR_PRIMARY : "#D1D5DB"} 
                                                    />
                                                </TouchableOpacity>
                                            );
                                        })}
                                    </ScrollView>
                                </View>
                            )}

                            <View style={styles.modalBtns}>
                                <TouchableOpacity
                                    style={styles.cancelBtn}
                                    onPress={() => { setCreateModalVisible(false); setNewSpaceTopic(''); }}
                                >
                                    <Text style={styles.cancelBtnText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.goLiveBtn}
                                    onPress={handleCreateSpace}
                                    disabled={creating}
                                >
                                    <LinearGradient
                                        colors={['#7c3aed', '#6d28d9']}
                                        style={styles.goLiveBtnGradient}
                                    >
                                        {creating ? (
                                            <ActivityIndicator size="small" color="#FFFFFF" />
                                        ) : (
                                            <>
                                                <Ionicons name="radio" size={16} color="#FFFFFF" />
                                                <Text style={styles.goLiveBtnText}>Start Space</Text>
                                            </>
                                        )}
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>
                        </ScrollView>
                    </TouchableOpacity>
                </KeyboardAvoidingView>
            </TouchableOpacity>
        </Modal>

            <MentorScheduleModal
                visible={scheduleModalVisible}
                onClose={() => setScheduleModalVisible(false)}
                onSchedule={handleScheduleSpace}
            />
        </View >
    );
};


// ── SpaceCard Sub-Component ───────────────────────────────────────────────
const SpaceCard = ({ space, onJoin, isJoining, label, onDelete }) => {
    // Determine status chip: live+mentor online → LIVE, live+mentor offline → MENTOR OFFLINE
    const mentorPresent = space.mentorOnline !== false && space.isLive;
    const isOffline = space.isLive && space.mentorOnline === false;

    return (
        <TouchableOpacity
            style={[
                styles.spaceCard,
                isOffline && styles.spaceCardOffline,
            ]}
            activeOpacity={0.88}
            onPress={onJoin}
            disabled={isJoining}
        >
            <View style={[styles.spaceEmojiBox, { backgroundColor: space.color + '18' }]}>
                <Text style={styles.spaceEmoji}>{space.emoji}</Text>
                {mentorPresent && <View style={styles.spaceCardLiveDot} />}
                {isOffline && <View style={[styles.spaceCardLiveDot, { backgroundColor: '#9CA3AF' }]} />}
            </View>

            <View style={styles.spaceCardContent}>
                <Text style={styles.spaceTopic} numberOfLines={1}>{space.topic}</Text>
                <Text style={styles.spaceMentor} numberOfLines={1}>{space.mentorName} • {space.mentorType}</Text>

                {space.isLive ? (
                    <View style={styles.spaceStats}>
                        {mentorPresent && (
                            <View style={styles.spaceStatItem}>
                                <Ionicons name="people-outline" size={12} color="#9CA3AF" />
                                <Text style={styles.spaceStatText}>{space.participantCount} listening</Text>
                            </View>
                        )}
                        {mentorPresent ? (
                            <View style={[styles.liveChip, { backgroundColor: space.color + '20', borderColor: space.color + '40' }]}>
                                <Text style={[styles.liveChipText, { color: space.color }]}>● LIVE</Text>
                            </View>
                        ) : (
                            <View style={[styles.liveChip, { backgroundColor: '#F3F4F620', borderColor: '#9CA3AF40' }]}>
                                <Ionicons name="moon-outline" size={9} color="#9CA3AF" style={{ marginRight: 3 }} />
                                <Text style={[styles.liveChipText, { color: '#9CA3AF' }]}>MENTOR OFFLINE</Text>
                            </View>
                        )}
                    </View>
                ) : (
                    <View style={styles.spaceStats}>
                        <Ionicons name="calendar-outline" size={12} color="#9CA3AF" />
                        <Text style={styles.spaceStatText}>{space.scheduledAt || 'Scheduled'}</Text>
                    </View>
                )}
            </View>

            <View style={styles.spaceJoinBtn}>
                {onDelete && (
                    <TouchableOpacity
                        style={styles.spaceDeleteBtn}
                        onPress={onDelete}
                        activeOpacity={0.7}
                    >
                        <Ionicons name="trash-outline" size={18} color="#ef4444" />
                    </TouchableOpacity>
                )}
                {isJoining ? (
                    <ActivityIndicator size="small" color={space.color} />
                ) : (
                    <View style={[styles.joinBtnInner, {
                        backgroundColor: mentorPresent || (label === 'Start') ? space.color : '#F3F4F6',
                        borderWidth: label === 'Start' ? 0 : (mentorPresent ? 0 : 1),
                        borderColor: '#E5E7EB',
                    }]}>
                        <Text style={[styles.joinBtnText, {
                            color: mentorPresent || (label === 'Start') ? '#FFFFFF' : '#6B7280',
                        }]}>
                            {label || (mentorPresent ? 'Join' : isOffline ? 'Wait' : 'Notify')}
                        </Text>
                    </View>
                )}
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: MENTOR_BG,
    },

    // Header
    headerWrapper: {
        overflow: 'hidden',
        paddingBottom: 16,
    },
    decorOrb1: {
        position: 'absolute',
        width: 200, height: 200,
        borderRadius: 100,
        backgroundColor: 'rgba(167,139,250,0.15)',
        right: -60, top: -60,
    },
    decorOrb2: {
        position: 'absolute',
        width: 120, height: 120,
        borderRadius: 60,
        backgroundColor: 'rgba(76,29,149,0.2)',
        left: -30, bottom: -20,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 8 : 14,
        paddingBottom: 12,
    },
    backBtn: {
        width: 40, height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center', justifyContent: 'center',
    },
    headerCenter: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    headerIconBadge: {
        width: 42, height: 42,
        borderRadius: 14,
        backgroundColor: 'rgba(196,181,253,0.2)',
        borderWidth: 1,
        borderColor: 'rgba(196,181,253,0.3)',
        alignItems: 'center', justifyContent: 'center',
    },
    headerTitle: {
        fontSize: 20, fontWeight: '800', color: '#FFFFFF',
    },
    headerSubtitle: {
        fontSize: 9, color: 'rgba(255,255,255,0.55)',
        fontWeight: '700', letterSpacing: 1.5,
    },
    createBtn: {
        width: 40, height: 40,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center', justifyContent: 'center',
    },
    // Tab Bar
    tabBar: {
        flexDirection: 'row',
        paddingHorizontal: 16,
        marginTop: 4,
    },
    tab: {
        flex: 1,
        paddingVertical: 12,
        alignItems: 'center',
        borderBottomWidth: 3,
        borderBottomColor: 'transparent',
        position: 'relative',
    },
    activeTab: {
        borderBottomColor: '#FFFFFF',
    },
    tabLabel: {
        fontSize: 14,
        fontWeight: '700',
        color: 'rgba(255,255,255,0.5)',
    },
    activeTabLabel: {
        color: '#FFFFFF',
    },
    tabBadge: {
        position: 'absolute',
        top: 10,
        right: '15%',
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#ef4444',
    },

    // Content
    scrollView: { flex: 1 },
    section: { paddingTop: 24, paddingHorizontal: 18, marginBottom: 8 },
    sectionHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 14,
    },
    liveIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    livePulse: {
        width: 10, height: 10,
        borderRadius: 5,
        backgroundColor: '#ef4444',
    },
    sectionTitle: {
        fontSize: 18, fontWeight: '800', color: '#111827',
    },

    // Space Card
    spaceCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 14,
        marginBottom: 12,
        gap: 12,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    spaceCardOffline: {
        backgroundColor: '#F9FAFB',
        borderColor: '#E5E7EB',
        opacity: 0.85,
    },
    spaceEmojiBox: {
        width: 52, height: 52,
        borderRadius: 16,
        alignItems: 'center', justifyContent: 'center',
        position: 'relative',
    },
    spaceEmoji: { fontSize: 26 },
    spaceCardLiveDot: {
        position: 'absolute',
        top: 4, right: 4,
        width: 10, height: 10,
        borderRadius: 5,
        backgroundColor: '#ef4444',
        borderWidth: 2, borderColor: '#FFFFFF',
    },
    spaceCardContent: { flex: 1 },
    spaceTopic: {
        fontSize: 14, fontWeight: '800', color: '#111827', marginBottom: 2,
    },
    spaceMentor: {
        fontSize: 12, color: '#6B7280', fontWeight: '500', marginBottom: 6,
    },
    spaceStats: {
        flexDirection: 'row', alignItems: 'center', gap: 6,
    },
    spaceStatItem: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
    },
    spaceStatText: {
        fontSize: 11, color: '#9CA3AF', fontWeight: '500',
    },
    liveChip: {
        paddingHorizontal: 8, paddingVertical: 2,
        borderRadius: 8, borderWidth: 1,
    },
    liveChipText: {
        fontSize: 10, fontWeight: '800',
    },
    spaceJoinBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    spaceDeleteBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: '#FEF2F2',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#FEE2E2',
    },
    joinBtnInner: {
        paddingHorizontal: 14, paddingVertical: 8,
        borderRadius: 10,
    },
    joinBtnText: {
        fontSize: 13, fontWeight: '800',
    },

    // Empty
    emptyState: {
        alignItems: 'center', paddingVertical: 30,
    },
    emptyEmoji: { fontSize: 40, marginBottom: 8 },
    emptyText: {
        fontSize: 15, fontWeight: '700', color: '#374151',
    },
    emptySubtext: {
        fontSize: 13, color: '#9CA3AF', marginTop: 4,
    },

    // CTA
    ctaSection: { paddingHorizontal: 18, marginTop: 8, marginBottom: 24 },
    ctaCard: {
        borderRadius: 22,
        padding: 24,
        alignItems: 'center',
        ...getShadow('md'),
    },
    ctaIconOrb: {
        width: 64, height: 64,
        borderRadius: 20,
        backgroundColor: '#7c3aed18',
        alignItems: 'center', justifyContent: 'center',
        marginBottom: 14,
    },
    ctaTitle: {
        fontSize: 18, fontWeight: '800', color: '#111827', marginBottom: 6,
    },
    ctaSubtitle: {
        fontSize: 13, color: '#6B7280',
        textAlign: 'center', lineHeight: 20, marginBottom: 20,
    },
    ctaBtn: {
        borderRadius: 14, overflow: 'hidden',
        ...getShadow('md'),
    },
    ctaBtnGradient: {
        flexDirection: 'row', alignItems: 'center',
        gap: 8, paddingHorizontal: 28, paddingVertical: 14,
    },
    ctaBtnText: {
        color: '#FFFFFF', fontSize: 15, fontWeight: '800',
    },

    // How section
    howSection: {
        paddingHorizontal: 18,
        marginBottom: 24,
    },
    // CTA List
    ctaSectionList: {
        paddingHorizontal: 18,
        paddingTop: 10,
        marginBottom: 24,
    },
    ctaList: {
        marginTop: 14,
        gap: 12,
    },
    ctaListItem: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 14,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    ctaListIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    ctaListContent: {
        flex: 1,
        marginLeft: 14,
    },
    ctaListLabel: {
        fontSize: 15,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 2,
    },
    ctaListSnippet: {
        fontSize: 12,
        color: '#6B7280',
        lineHeight: 16,
    },

    // Sticky Footer
    stickyFooter: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
        paddingTop: 14,
        ...getShadow('lg'),
    },
    stickyHowRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        marginBottom: 10,
        gap: 8,
    },
    stickyHowCard: {
        flex: 1,
        alignItems: 'center',
        backgroundColor: '#FAFBFF',
        borderRadius: 14,
        paddingVertical: 10,
        paddingHorizontal: 4,
        borderWidth: 1,
        ...getShadow('xs'),
    },
    stickyHowIconBox: {
        width: 32,
        height: 32,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 5,
    },
    stickyHowLabel: {
        fontSize: 10,
        fontWeight: '800',
        color: '#1F2937',
        textAlign: 'center',
        letterSpacing: 0.2,
        marginTop: 2,
    },
    stickyHowDesc: {
        fontSize: 9,
        color: '#9CA3AF',
        textAlign: 'center',
        marginTop: 1,
        lineHeight: 13,
    },
    footerTextSticky: {
        textAlign: 'center',
        color: '#94A3B8',
        fontWeight: '600',
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },

    // Create Modal Styles
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    keyboardAvoidingView: {
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    createModal: {
        width: SCREEN_WIDTH - 40,
        maxHeight: SCREEN_HEIGHT * 0.8,
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 24,
        paddingVertical: 20,
        ...getShadow('lg'),
    },
    modalHandle: {
        width: 40,
        height: 5,
        backgroundColor: '#E5E7EB',
        borderRadius: 3,
        alignSelf: 'center',
        marginBottom: 20,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    modalSubtitle: {
        fontSize: 14,
        color: '#6B7280',
        marginBottom: 24,
    },
    topicInput: {
        backgroundColor: '#F9FAFB',
        borderWidth: 1.5,
        borderColor: '#F1F5F9',
        borderRadius: 16,
        padding: 16,
        fontSize: 16,
        color: '#111827',
        marginBottom: 24,
    },
    modalBtns: {
        flexDirection: 'row',
        gap: 12,
    },
    cancelBtn: {
        flex: 1,
        paddingVertical: 16,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F3F4F6',
    },
    cancelBtnText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#6B7280',
    },
    goLiveBtn: {
        flex: 2,
        borderRadius: 14,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    goLiveBtnGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 16,
    },
    goLiveBtnText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#FFFFFF',
    },

    // Focus Group Selection Styles
    userSearchBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F3F4F6',
        borderRadius: 12,
        marginBottom: 12,
    },
    userSearchInput: {
        flex: 1,
        paddingVertical: 10,
        paddingHorizontal: 12,
        fontSize: 14,
        color: '#111827',
    },
    selectedUsersContainer: {
        flexDirection: 'row',
        marginBottom: 12,
    },
    userChip: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: MENTOR_PRIMARY,
        borderRadius: 20,
        paddingVertical: 6,
        paddingHorizontal: 12,
        marginRight: 8,
    },
    userChipText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '600',
    },
    searchResultsContainer: {
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        marginBottom: 16,
        overflow: 'hidden',
    },
    searchResultItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
    },
    searchResultLeft: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    searchResultAvatar: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: '#E5E7EB',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
    },
    searchResultName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111827',
    },
    searchResultSub: {
        fontSize: 11,
        color: '#6B7280',
    },
});

export default MentorSpacesScreen;
